import { useSelector } from 'react-redux';
import { Trans, useTranslation } from 'react-i18next';

import type { QuestionConfigQuestionType } from '/index';

import formatTonnage from '/shared/functions/formatTonnage';

import { QUESTION_BRANCH_ACTIVITY } from '/shared/util/Statics';
import { urlApplicationRoot } from '/business/shared/util/Statics';
import useUrlBuilder from '/business/shared/hooks/useUrlBuilder';
import getApplicationIdSelector from '/business/shared/state/selectors/application/getApplicationIdSelector';
import StyledHeading from '/shared/styled/components/StyledHeading';
import StyledInfoFlash from '/shared/styled/StyledInfoFlash';
import StyledParagraph from '/shared/styled/components/StyledParagraph';
import getElectTotalCarbonEmissions from '/business/shared/state/selectors/application/electricityHeatSteam/getElectTotalCarbonEmissions';
import getElectConsumptionDataStatsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectConsumptionDataStatsSelector';
import getElectDecoratedConsumptionDataSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectDecoratedConsumptionDataSelector';
import getElectNumLocationsSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsSelector';
import getElectNumLocationsInputtedSelector from '/business/shared/state/selectors/application/electricityHeatSteam/getElectNumLocationsInputtedSelector';
import isApplicationAnswersEditableSelector from '/business/shared/state/selectors/application/workflow/isApplicationAnswersEditableSelector';

import ReportingPeriod from '/business/shared/components/widgets/reporting-period/ReportingPeriod';
import EducationWidget from '/business/shared/components/widgets/education/EducationWidget';
import LinkButton from '/shared/components/widgets/button/LinkButton';
import ConsumptionTable from './components/ConsumptionTable';

import StyledElecBreakout from './components/__styled__/StyledElecBreakout';
import StyledElecEmptyState from './components/__styled__/StyledElecEmptyState';
import StyledInfoList, { StyledInfoListItem } from './components/__styled__/StyledInfoList';

const addPageLink = `/${urlApplicationRoot}/questions/calculation/scope-2/electricity-heat-steam/consumption-data/consumption-data-add`;

export default function ElectricityHeatSteamConsumption({ question }: { question: QuestionConfigQuestionType }) {
  const { t } = useTranslation();

  const appId = useSelector(getApplicationIdSelector);
  const { getUrlWithAppId } = useUrlBuilder();
  const totalCarbonEmissions = useSelector(getElectTotalCarbonEmissions);
  const consumptionData = useSelector(getElectDecoratedConsumptionDataSelector);
  const numLocations = useSelector(getElectNumLocationsSelector);
  const numLocationsInputted = useSelector(getElectNumLocationsInputtedSelector);
  const { totalKwhCoveredByCertificate, totalKwhNotCoveredByCertificate, totalKwhOnsiteRenewables, certificateTotal } =
    useSelector(getElectConsumptionDataStatsSelector);
  const isEditable = useSelector(isApplicationAnswersEditableSelector);
  const totalKwhConsumption = totalKwhCoveredByCertificate + totalKwhNotCoveredByCertificate + totalKwhOnsiteRenewables;
  const formattedTotalCarbonEmissions = formatTonnage(totalCarbonEmissions);
  const kwhLabel = t('business:emissionFactors.electricity.kwh');
  const tonnageLabel = t('business:emissionFactors.emissionUnits.tonnageRaw');

  const hasConsumptionData = consumptionData.length > 0;

  return (
    <>
      <EducationWidget question={question} branchType={QUESTION_BRANCH_ACTIVITY} />

      <div>
        <StyledHeading as="h1" $type="h1">
          {/* @i18n: Electricity, heat and steam - Consumption data */}
          <Trans i18nKey="questions:electricityHeatSteam.consumption.title" />
        </StyledHeading>
        <StyledParagraph $largeText $hasExtraMarginBottom>
          {/* @i18n: Enter the consumption details for any electricity, heat or steam used by your business[...] */}
          <Trans i18nKey="questions:electricityHeatSteam.consumption.intro" components={{ 1: <ReportingPeriod /> }} />
        </StyledParagraph>
        <StyledInfoFlash>
          <div>
            {/* @i18n: For more information, refer to the "Help" section for detailed guidance. */}
            <Trans i18nKey="questions:electricityHeatSteam.shared.helpText" />
          </div>
        </StyledInfoFlash>
      </div>

      {hasConsumptionData ? (
        <>
          <LinkButton disabled={!isEditable} $primary to={getUrlWithAppId(addPageLink, appId)}>
            {/* @i18n: Add new consumption data */}
            <Trans i18nKey="questions:electricityHeatSteam.consumption.addNewConsumptionData" />
          </LinkButton>
          <StyledElecBreakout>
            <StyledInfoList>
              <StyledInfoListItem>
                <div>{t('questions:electricityHeatSteam.shared.consumptionDataAddedFor')}</div>
                <div>
                  <div>
                    {/* @i18n: 1 out of 3 locations */}
                    <Trans
                      i18nKey="questions:electricityHeatSteam.shared.outOfLocationsInline"
                      values={{ numLocationsInputted, numLocations }}
                      components={{ 1: <span /> }}
                    />
                  </div>
                </div>
              </StyledInfoListItem>
              <StyledInfoListItem>
                <div>{t('questions:electricityHeatSteam.consumption.totalEnergyConsumption')}</div>
                <div>
                  <div>{formatTonnage(totalKwhConsumption)}</div>
                  <div>{kwhLabel}</div>
                </div>
              </StyledInfoListItem>
              <StyledInfoListItem>
                <div>{t('questions:electricityHeatSteam.consumption.energyNotCovered')}</div>
                <div>
                  <div>{formatTonnage(totalKwhConsumption - certificateTotal)}</div>
                  <div>{kwhLabel}</div>
                </div>
              </StyledInfoListItem>
              <StyledInfoListItem>
                <div>{t('questions:electricityHeatSteam.shared.calculatedCarbonEmissions')}</div>
                <div>
                  <div>{formattedTotalCarbonEmissions}</div>
                  <div>{tonnageLabel}</div>
                </div>
              </StyledInfoListItem>
            </StyledInfoList>
            <StyledHeading as="h2" $type="h2" $hasExtraTopMargin $hasExtraBottomMargin>
              {/* @i18n: Calculated carbon emissions for electricity, heat and steam: 10 tCO₂e tonnes */}
              <Trans
                i18nKey="questions:electricityHeatSteam.consumption.calculatedEmissionsHeading"
                values={{ formattedTotalCarbonEmissions, tonnageLabel }}
                components={{ 1: <strong /> }}
                shouldUnescape
              />
            </StyledHeading>
            <ConsumptionTable />
          </StyledElecBreakout>
        </>
      ) : (
        <StyledElecEmptyState>
          <StyledHeading as="h2" $type="h2">
            {/* @i18n: Add consumption data */}
            <Trans i18nKey="questions:electricityHeatSteam.consumption.emptyTitle" />
          </StyledHeading>
          <StyledParagraph $smallText $hasExtraMarginBottom>
            {/* @i18n: Enter the consumption details for any electricity, heat or steam used by your business[...] */}
            <Trans i18nKey="questions:electricityHeatSteam.consumption.emptyBody" />
          </StyledParagraph>
          <LinkButton disabled={!isEditable} $primary to={getUrlWithAppId(addPageLink, appId)}>
            {/* @i18n: Add new consumption data */}
            <Trans i18nKey="questions:electricityHeatSteam.consumption.addNewConsumptionData" />
          </LinkButton>
        </StyledElecEmptyState>
      )}
    </>
  );
}
